import React, { useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { AvailabilityPicker } from '@/components/schedule/availability-picker';
import { TimeField } from '@/components/schedule/time-field';
import { MemberAvatar, getFirstName } from '@/components/team/member-avatar';
import { useAvailability, useProviderAppointmentAction } from '@/hooks/use-appointments';
import { useProviderClients } from '@/hooks/use-clients';
import { useMembership } from '@/hooks/use-membership';
import { useProviderProfile } from '@/hooks/use-provider';
import { useProviderServices, useServicesOf } from '@/hooks/use-services';
import { useTeamMembers } from '@/hooks/use-team';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { showPlanLimit } from '@/store/planLimitStore';
import { AvailabilitySlot } from '@/types/appointment';
import { useFormFooterInset } from '@/components/mobile-bottom-nav';
import { Client } from '@/types/client';
import {
  dayParts,
  formatDuration,
  formatSlotTime,
  zonedDateKey,
  zonedLocalToIso,
} from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

const HORIZON_DAYS = 30;

function Section({
  step,
  title,
  children,
  done,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
  done?: boolean;
}) {
  const th = useThemedHex();
  return (
    <View className="gap-3">
      <View className="flex-row items-center gap-2">
        <View
          className="h-6 w-6 items-center justify-center rounded-full"
          style={{ backgroundColor: done ? th('#ECFDF5') : th('#F5F5F5') }}
        >
          {done ? (
            <Ionicons className="text-emerald-700" name="checkmark" size={14} />
          ) : (
            <ThemedText className="font-bold text-xs text-neutral-500">{step}</ThemedText>
          )}
        </View>
        <ThemedText className="font-bold text-base text-neutral-900">{title}</ThemedText>
      </View>
      {children}
    </View>
  );
}

export default function NewAppointmentScreen() {
  const th = useThemedHex();
  const { addToast } = useToastStore();
  const bottomInset = useFormFooterInset();
  const provider = useProviderProfile();
  const action = useProviderAppointmentAction();

  // Vindo da visão "Equipe": o dono escolhe com quem antes de tudo, e os
  // serviços, a disponibilidade e a marcação são daquela pessoa — sem trocar
  // o foco do app.
  const params = useLocalSearchParams<{ team?: string; professional?: string }>();
  const { can } = useMembership();
  const teamMode = params.team === '1' && can('actAs');
  const members = useTeamMembers({ enabled: teamMode });
  const professionals = useMemo(
    () => (members.data ?? []).filter((m) => m.status === 'ACTIVE' && m.attends),
    [members.data],
  );
  const [professionalId, setProfessionalId] = useState<string | null>(params.professional ?? null);
  const professional = professionals.find((m) => m.providerId === professionalId) ?? null;

  const [search, setSearch] = useState('');
  const clients = useProviderClients(search);
  const ownServices = useProviderServices({ limit: 100 });
  const teamServices = useServicesOf(teamMode ? professionalId : null);
  const servicesLoading = teamMode ? teamServices.isLoading : ownServices.isLoading;

  const [client, setClient] = useState<Client | null>(null);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [overbook, setOverbook] = useState(false);
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null);
  // Encaixe manual:
  const days = useMemo(
    () =>
      Array.from({ length: HORIZON_DAYS }, (_, i) =>
        zonedDateKey(new Date(Date.now() + i * 86400000).toISOString()),
      ),
    [],
  );
  const [fitDate, setFitDate] = useState(days[0]);
  const [fitTime, setFitTime] = useState('09:00');

  const schedulableServices = useMemo(
    () =>
      (teamMode ? (teamServices.data ?? []) : (ownServices.data?.data ?? [])).filter(
        (s) => !!s.durationMinutes,
      ),
    [teamMode, teamServices.data, ownServices.data],
  );
  const selectedService = schedulableServices.find((s) => s.id === serviceId);

  const range = useMemo(() => {
    const fromD = new Date();
    const toD = new Date(fromD.getTime() + HORIZON_DAYS * 86400000);
    return { from: fromD.toISOString(), to: toD.toISOString() };
  }, []);

  const targetProviderId = teamMode ? (professionalId ?? undefined) : provider.data?.id;

  const availability = useAvailability({
    providerId: overbook ? undefined : targetProviderId,
    serviceId: overbook ? undefined : (serviceId ?? undefined),
    from: range.from,
    to: range.to,
  });

  const startAt = overbook ? zonedLocalToIso(fitDate, fitTime) : (slot?.startAt ?? null);

  const canSubmit = !!client && !!serviceId && !!startAt && (!teamMode || !!professional);

  const pickProfessional = (providerId: string) => {
    if (providerId === professionalId) return;
    setProfessionalId(providerId);
    // Serviço e horário são da pessoa: trocar de pessoa zera os dois.
    setServiceId(null);
    setSlot(null);
  };

  const submit = () => {
    if (!client || !serviceId || !startAt) return;
    if (teamMode && !professional) return;
    action.create.mutate(
      {
        clientId: client.id,
        serviceId,
        startAt,
        force: overbook,
        actingProviderId: teamMode ? (professional?.providerId ?? undefined) : undefined,
      },
      {
        onSuccess: () => {
          const withWhom =
            teamMode && professional ? ` com ${getFirstName(professional.name)}` : '';
          addToast(
            overbook ? `Encaixe criado${withWhom}.` : `Agendamento confirmado${withWhom}.`,
            'success',
          );
          router.replace('/(tabs)/(provider)/schedule');
        },
        onError: (e: any) => {
          if (!showPlanLimit(e))
            addToast(getApiErrorMessage(e, 'Não foi possível marcar.'), 'error');
        },
      },
    );
  };

  const clientList = clients.data ?? [];

  return (
    <PageLayout
      title="Marcar agendamento"
      subtitle="Para um cliente da sua base."
      onBack={() => router.back()}
      footer={
        <View
          className="border-t border-neutral-200 bg-card px-4 pt-4"
          style={{ paddingBottom: 16 + bottomInset }}
        >
          <PressableScale
            haptic
            disabled={!canSubmit || action.create.isPending}
            onPress={submit}
            className="w-full flex-row items-center justify-center gap-2 rounded-full py-4"
            style={{
              backgroundColor: canSubmit ? '#FD5001' : th('#D4D4D4'),
              borderCurve: 'continuous',
            }}
          >
            {action.create.isPending ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Ionicons name="checkmark-circle" size={20} color="#fff" />
                <ThemedText className="font-bold text-base text-white">
                  {startAt ? `Marcar ${formatSlotTime(startAt)}` : 'Marcar'}
                </ThemedText>
              </>
            )}
          </PressableScale>
        </View>
      }
    >
      {/* Com quem (só pela visão Equipe) */}
      {teamMode && (
        <Section step={1} title="Com quem?" done={!!professional}>
          {members.isLoading ? (
            <ActivityIndicator color="#FD5001" className="py-2" />
          ) : (
            <View className="flex-row flex-wrap gap-2">
              {professionals.map((m) => {
                const active = m.providerId === professionalId;
                return (
                  <PressableScale
                    key={m.id}
                    haptic
                    onPress={() => pickProfessional(m.providerId)}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: active }}
                    className="h-11 flex-row items-center gap-2 rounded-full pl-1.5 pr-4"
                    style={{
                      backgroundColor: active ? '#FD5001' : th('#fff'),
                      borderWidth: 1,
                      borderColor: active ? '#FD5001' : th('#E5E5E5'),
                      borderCurve: 'continuous',
                    }}
                  >
                    <MemberAvatar name={m.name} image={m.image} color={m.color} size={30} />
                    <ThemedText
                      className="font-semibold text-sm"
                      style={{ color: active ? '#fff' : th('#171717') }}
                    >
                      {m.role === 'OWNER' ? 'Você' : getFirstName(m.name)}
                    </ThemedText>
                  </PressableScale>
                );
              })}
            </View>
          )}
        </Section>
      )}

      {/* Cliente */}
      <Section step={teamMode ? 2 : 1} title="Cliente" done={!!client}>
        {client ? (
          <PressableScale
            onPress={() => setClient(null)}
            className="flex-row items-center justify-between rounded-2xl border border-neutral-200 bg-card px-4 py-3"
          >
            <View className="flex-row items-center gap-2">
              <Ionicons name="person-circle-outline" size={22} color="#FD5001" />
              <ThemedText className="font-semibold text-neutral-900">
                {client.user?.username ?? 'Cliente'}
              </ThemedText>
            </View>
            <ThemedText className="font-semibold text-xs text-primary">Trocar</ThemedText>
          </PressableScale>
        ) : (
          <View className="gap-2">
            <View className="flex-row items-center gap-2 rounded-2xl border border-neutral-200 bg-card px-3.5 py-2.5">
              <Ionicons className="text-neutral-400" name="search" size={18} />
              <TextInput
                value={search}
                onChangeText={setSearch}
                placeholder="Buscar cliente pelo nome"
                placeholderTextColor="#a3a3a3"
                className="flex-1 text-sm text-neutral-900"
              />
            </View>
            {clients.isLoading ? (
              <ActivityIndicator color="#FD5001" className="py-2" />
            ) : (
              clientList.slice(0, 8).map((c) => (
                <PressableScale
                  key={c.id}
                  onPress={() => {
                    setClient(c);
                    setSearch('');
                  }}
                  className="flex-row items-center gap-2 rounded-xl bg-card px-3.5 py-3"
                >
                  <Ionicons className="text-neutral-500" name="person-outline" size={16} />
                  <ThemedText className="text-sm text-neutral-800">
                    {c.user?.username ?? 'Cliente'}
                  </ThemedText>
                </PressableScale>
              ))
            )}
            {!clients.isLoading && clientList.length === 0 && (
              <ThemedText className="px-1 text-xs text-neutral-400">
                Nenhum cliente encontrado.
              </ThemedText>
            )}
          </View>
        )}
      </Section>

      {/* Serviço */}
      <Section step={teamMode ? 3 : 2} title="Serviço" done={!!serviceId}>
        {teamMode && !professional ? (
          <ThemedText className="text-sm text-neutral-400">Escolha com quem primeiro.</ThemedText>
        ) : servicesLoading ? (
          <ActivityIndicator color="#FD5001" className="py-2" />
        ) : schedulableServices.length === 0 ? (
          <ThemedText className="text-sm text-neutral-500">
            Nenhum serviço agendável. Defina uma duração em Configurar agenda › Serviços.
          </ThemedText>
        ) : (
          <View className="flex-row flex-wrap gap-2">
            {schedulableServices.map((s) => {
              const active = s.id === serviceId;
              return (
                <PressableScale
                  key={s.id}
                  haptic
                  onPress={() => {
                    setServiceId(s.id);
                    setSlot(null);
                  }}
                  className="rounded-full px-4 py-2.5"
                  style={{
                    backgroundColor: active ? '#FD5001' : th('#fff'),
                    borderWidth: 1,
                    borderColor: active ? '#FD5001' : th('#E5E5E5'),
                    borderCurve: 'continuous',
                  }}
                >
                  <ThemedText
                    className="font-semibold text-sm"
                    style={{ color: active ? '#fff' : th('#171717') }}
                  >
                    {s.name}
                  </ThemedText>
                  <ThemedText
                    className="text-[10px]"
                    style={{ color: active ? 'rgba(255,255,255,0.85)' : th('#a3a3a3') }}
                  >
                    {formatDuration(s.durationMinutes)}
                  </ThemedText>
                </PressableScale>
              );
            })}
          </View>
        )}
      </Section>

      {/* Horário */}
      <Section step={teamMode ? 4 : 3} title="Horário" done={!!startAt}>
        {/* Modo encaixe */}
        <PressableScale
          haptic
          onPress={() => {
            setOverbook((v) => !v);
            setSlot(null);
          }}
          className="flex-row items-center gap-2 self-start rounded-full px-3 py-1.5"
          style={{ backgroundColor: overbook ? th('#FEF3C7') : th('#F5F5F5') }}
        >
          <Ionicons
            name={overbook ? 'flash' : 'flash-outline'}
            size={14}
            color={overbook ? th('#B45309') : th('#737373')}
          />
          <ThemedText
            className="font-semibold text-xs"
            style={{ color: overbook ? th('#B45309') : th('#737373') }}
          >
            Encaixe (furar as regras)
          </ThemedText>
        </PressableScale>

        {!serviceId ? (
          <ThemedText className="text-sm text-neutral-400">Escolha um serviço primeiro.</ThemedText>
        ) : overbook ? (
          <View className="gap-3">
            <ThemedText className="text-xs text-amber-700">
              O encaixe ignora grade, bloqueios e capacidade. Use com cuidado.
            </ThemedText>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 8, paddingVertical: 2 }}
            >
              {days.map((key) => {
                const active = key === fitDate;
                const parts = dayParts(key);
                return (
                  <PressableScale
                    key={key}
                    onPress={() => setFitDate(key)}
                    className="items-center rounded-2xl px-3 py-2"
                    style={{
                      minWidth: 54,
                      backgroundColor: active ? '#FD5001' : th('#fff'),
                      borderCurve: 'continuous',
                    }}
                  >
                    <ThemedText
                      className="font-medium text-[11px]"
                      style={{ color: active ? 'rgba(255,255,255,0.85)' : th('#737373') }}
                    >
                      {parts.weekday}
                    </ThemedText>
                    <ThemedText
                      className="font-bold text-base"
                      style={{ color: active ? '#fff' : th('#171717') }}
                    >
                      {parts.day}
                    </ThemedText>
                  </PressableScale>
                );
              })}
            </ScrollView>
            <View className="flex-row items-center gap-2">
              <ThemedText className="text-sm text-neutral-500">Início às</ThemedText>
              <TimeField value={fitTime} onChange={setFitTime} />
            </View>
          </View>
        ) : availability.isLoading ? (
          <ActivityIndicator color="#FD5001" className="py-4" />
        ) : (availability.data?.days ?? []).some((d) => d.available && d.slots.length > 0) ? (
          <AvailabilityPicker
            days={availability.data?.days ?? []}
            selectedStart={slot?.startAt ?? null}
            onSelect={setSlot}
            showCapacity={(selectedService?.slotCapacity ?? 1) > 1}
          />
        ) : (
          <View className="items-center gap-2 rounded-2xl bg-card p-6">
            <Ionicons className="text-neutral-400" name="calendar-clear-outline" size={32} />
            <ThemedText className="text-center text-sm text-neutral-500">
              Sem horários livres no período. Use o encaixe para marcar mesmo assim.
            </ThemedText>
          </View>
        )}
      </Section>
    </PageLayout>
  );
}
